import { t } from '../../../../../services/texts.service';
import { memo, useRef } from 'react';
import ValidationInfo from '../../../../../validation/validation-info';
import { IMultiFacetSelector } from '../../../../Shared/MultiFacetSelector';
import MultiFacetSelector from '../../../../Shared/MultiFacetSelector';
import IMultiFacet from '../../../../../models/multi-facet.model';
import { MultiFacetType } from '../../../../../models/multi-facet-type.enum';
import TransportModeFilter from '../../../../Shared/TransportModeFilter';
import { TransportMode } from '../../../../../models/transport-mode.enum';
import { ILine } from '../../../../../models/line.model';
import { DateTime } from 'luxon';
import { AnalyticsService } from '../../../../../services/analytics-service';
import { IconMotif } from '../../../../Shared/Icon/icon-motif.enum';
import { Icon } from '../../../../Shared/Icon';
import { SanitizerService } from '../../../../../services/sanitizer.service';
import './style.scss';
import { AutocompleteLabelPosition } from '../../../../Shared/AutocompleteBase/autocomplete-base.enum';

function MapSettings({
  informationTextHtml,
  className,
  id,
  selectedModes,
  selectedLines,
  onTransportModesChange,
  onLinesChange,
  onClose,
}: {
  informationTextHtml: string;
  className?: string;
  id?: string;
  selectedModes: TransportMode[];
  selectedLines: IMultiFacet[];
  onTransportModesChange: (transportModes: TransportMode[]) => void;
  onLinesChange: (multiFacetItems: IMultiFacet[]) => void;
  onClose: () => void;
}) {
  const availableTransportModes = [TransportMode.tram, TransportMode.bus, TransportMode.ferry, TransportMode.train];

  const multiFacetSelectorRef = useRef<IMultiFacetSelector>(null);

  const isNoModeSelected = selectedModes.length === 0;

  // NV-8622: Hide specific ferry lines that currently lack live MQTT data.
  const hiddenFerryLineGids = new Set<string>(['9011091029700000', '9011091029800000', '9011091029900000', '9011091038700000', '9011032032200000']);
  function filterLineResults(line: ILine): boolean {
    // The default hideFutureLines from MultiFacetAutocomplete
    if (!(!!line.appliesFrom && line.appliesFrom <= DateTime.now())) {
      return false;
    }

    // NV-8526: Hide "anropsstyrd trafik"
    if (line.transportMode === TransportMode.taxi) {
      return false;
    }

    if (line.transportMode === TransportMode.ferry && line.gid !== undefined && hiddenFerryLineGids.has(line.gid)) {
      return false;
    }

    return true;
  }

  function handleTrackingOfTransportModes(previousModes: TransportMode[], nextModes: TransportMode[]) {
    const removedMode = previousModes.find((mode) => !nextModes.includes(mode));
    if (removedMode === undefined) return;

    switch (removedMode) {
      case TransportMode.tram:
        AnalyticsService.addEvent(AnalyticsService.VehiclePositionsMap, 'Klicka ur Spårvagn');
        return;
      case TransportMode.bus:
        AnalyticsService.addEvent(AnalyticsService.VehiclePositionsMap, 'Klicka ur Buss');
        return;
      case TransportMode.ferry:
        AnalyticsService.addEvent(AnalyticsService.VehiclePositionsMap, 'Klicka ur Båt');
        return;
      case TransportMode.train:
        AnalyticsService.addEvent(AnalyticsService.VehiclePositionsMap, 'Klicka ur Tåg');
        return;
      default:
        return;
    }
  }

  return (
    <div id={id} className={`${className ?? ''}`}>
      <div className="mt-2 mt-lg-3 mx-2 mx-lg-3">
        <div className="justify-content-between align-items-center d-none d-lg-flex">
          <h2 className="h2">{t('Filter', 'Filter')}</h2>
          <button className="btn-close" type="button" aria-label={t.close} onClick={() => onClose()}></button>
        </div>
        <div className="d-flex mb-2 justify-content-center">
          <button className="btn btn-icon-only d-lg-none mt-0" type="button" aria-label={t.close} onClick={() => onClose()}>
            <Icon motif={IconMotif.ChevronDown} altText="" />
          </button>
        </div>
        <div className="">
          <MultiFacetSelector
            id="multi-facet-selector"
            ref={multiFacetSelectorRef}
            multiFacetTypes={[MultiFacetType.Line]}
            placeholderText={t('Välj linje', 'Select line')}
            noResultsText={t('Inga sökresultat', 'No search results')}
            filterLineResults={filterLineResults}
            onChange={(newSelectedLines) => onLinesChange(newSelectedLines)}
            state={{ value: selectedLines, validationInfo: new ValidationInfo() }}
            labelText={t('Linje', 'Line')}
            labelPosition={AutocompleteLabelPosition.hide}
          />
        </div>
        <fieldset className="" aria-describedby={isNoModeSelected ? 'transport-mode-error' : undefined}>
          <legend className="visually-hidden">{t('Trafikslag', 'Transport modes')}</legend>
          <TransportModeFilter
            availableTransportModes={availableTransportModes}
            groupNonVasttagenAsOtherTrains={true}
            selectedTransportModes={selectedModes}
            isInvalid={isNoModeSelected}
            onChange={(transportModes) => {
              onTransportModesChange(transportModes);
              handleTrackingOfTransportModes(selectedModes, transportModes);
            }}
            className="py-3 gap-1"
            renderAsChips={true}
          />
          {isNoModeSelected && (
            <div className="invalid-feedback d-block mb-3" id="transport-mode-error" role="alert">
              <div>{t('Välj ett trafikslag', 'Select a transport mode')}</div>
            </div>
          )}
        </fieldset>
      </div>
      {informationTextHtml && (
        <div className="mx-0 mx-lg-3">
          <h3 className="d-none d-lg-block">{t('Hur fungerar kartan?', 'How does the map work?')}</h3>
          <h3 className="d-lg-none visually-hidden">{t('Hur fungerar kartan?', 'How does the map work?')}</h3>
          <div
            className="py-3 p-lg-0 d-flex justify-content-center justify-content-lg-start last-child-margin-bottom-none map-settings__cms-text-area"
            dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(informationTextHtml) }}
          ></div>
        </div>
      )}
    </div>
  );
}

export default memo(MapSettings);
